import { useSyncExternalStore } from 'react';

// Passive localStorage history of restaurants the user opened. Shaped like
// SuggestionListItem's Suggestion so the search dropdown can render entries
// directly.
export interface RecentlyViewedEntry {
  id: string | number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  inspectionForks?: number;
  viewedAt: number;
}

const STORAGE_KEY = 'foodweb_recently_viewed';
const MAX_ENTRIES = 8;

// Module-level shared store so all hook instances share state (same pattern
// as useTheme).
let entries: RecentlyViewedEntry[] = readFromStorage();
const listeners = new Set<() => void>();

const EMPTY: RecentlyViewedEntry[] = [];

function readFromStorage(): RecentlyViewedEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentlyViewedEntry =>
        !!e && typeof e === 'object' && 'id' in e && typeof e.name === 'string'
    );
  } catch {
    return [];
  }
}

function persist(next: RecentlyViewedEntry[]) {
  entries = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or unavailable — keep the in-memory list working
  }
  listeners.forEach((fn) => fn());
}

function getSnapshot(): RecentlyViewedEntry[] {
  return entries;
}

function getServerSnapshot(): RecentlyViewedEntry[] {
  return EMPTY;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

// Write path — plain function so non-React code (App's selection handler)
// can record views without the hook.
export function recordRecentlyViewed(entry: Omit<RecentlyViewedEntry, 'viewedAt'>): void {
  if (typeof window === 'undefined') return;
  if (!entry.id || !entry.name) return;
  const withoutDuplicate = entries.filter((e) => String(e.id) !== String(entry.id));
  persist([{ ...entry, viewedAt: Date.now() }, ...withoutDuplicate].slice(0, MAX_ENTRIES));
}

export function clearRecentlyViewed(): void {
  if (typeof window === 'undefined') return;
  persist([]);
}

export function useRecentlyViewed(): RecentlyViewedEntry[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
